import i18next from "i18next";

import { Dictionary } from "@/core/i18n";
import { BillingPeriod } from "utils/billingPeriod.helpers";

export type MembershipBillingTerm = {
  billingPeriodInterval: BillingPeriod;
  billingPeriodIntervalCount: number;
};

const firstBillingPeriodTranslationKeys: Record<
  BillingPeriod,
  { singular: keyof Dictionary; plural: keyof Dictionary }
> = {
  [BillingPeriod.Daily]: {
    singular: "membershipFirstBillingPeriod.firstDayOfMembership",
    plural: "membershipFirstBillingPeriod.firstDaysOfMembership",
  },
  [BillingPeriod.Weekly]: {
    singular: "membershipFirstBillingPeriod.firstWeekOfMembership",
    plural: "membershipFirstBillingPeriod.firstWeeksOfMembership",
  },
  [BillingPeriod.Monthly]: {
    singular: "membershipFirstBillingPeriod.firstMonthOfMembership",
    plural: "membershipFirstBillingPeriod.firstMonthsOfMembership",
  },
  [BillingPeriod.Yearly]: {
    singular: "membershipFirstBillingPeriod.firstYearOfMembership",
    plural: "membershipFirstBillingPeriod.firstYearsOfMembership",
  },
};

/**
 * Names the stretch of membership a membership discount pays for, as the object of a sentence
 * ("the first year of membership").
 *
 * A discount lowers only the checkout charge, which pays for the membership's whole first billing
 * period; renewals bill at full price. Memberships on different periods share one phrase naming the
 * first billing period of each.
 */
export const formatMembershipFirstBillingPeriod = (
  terms: readonly MembershipBillingTerm[],
): string => {
  if (terms.length === 0) {
    throw new Error("A membership's first billing period needs at least one membership.");
  }

  const invalidTerm = terms.find((term) => term.billingPeriodIntervalCount < 1);
  if (invalidTerm) {
    throw new Error(
      `A membership bills every ${invalidTerm.billingPeriodIntervalCount} periods; expected at least 1.`,
    );
  }

  const [{ billingPeriodInterval, billingPeriodIntervalCount }] = terms;
  const sharesOnePeriod = terms.every(
    (term) =>
      term.billingPeriodInterval === billingPeriodInterval &&
      term.billingPeriodIntervalCount === billingPeriodIntervalCount,
  );
  if (!sharesOnePeriod) {
    return i18next.t("membershipFirstBillingPeriod.firstBillingPeriodOfEachMembership");
  }

  const keys = firstBillingPeriodTranslationKeys[billingPeriodInterval];
  if (!keys) {
    throw new Error(`Unknown membership billing period interval: ${billingPeriodInterval}.`);
  }
  return billingPeriodIntervalCount === 1
    ? i18next.t(keys.singular)
    : i18next.t(keys.plural, { count: billingPeriodIntervalCount });
};
